// DrawEllipseTool — click+drag to draw an ellipse, then create a shape via callback
// Same interaction as DrawRectTool but with ellipse preview

import * as THREE from 'three'
import type { Tool, ToolContext } from '@/types/editor'
import type { ToolManager } from '../ToolManager'
import type { DimensionOverlay } from '@/ui/DimensionOverlay'
import { DPM } from '@/constants/coordinates'
import { getViewer, getToolMode } from '@/ui/state'
import { debug } from '@/utils/debug'

export interface DrawEllipseConfig {
  /** Called when drawing finishes. Returns new shape ID. */
  onCreate: (rect: { x: number; y: number; width: number; height: number }) => string
  /** Preview color (default: 0x4ecca3) */
  previewColor?: number
  /** Optional dimension overlay for live measurements */
  overlay?: DimensionOverlay
}

/** Minimum size in world meters to create a shape */
const MIN_SIZE_WORLD = 0.5

/** Number of segments for ellipse curve */
const ELLIPSE_SEGMENTS = 48

export class DrawEllipseTool implements Tool {
  readonly name = 'draw-ellipse'
  readonly cursor = 'crosshair'
  readonly hint = 'tool.hint.drawEllipse'

  private toolManager: ToolManager
  private config: DrawEllipseConfig

  // Drawing state
  private isDrawing = false
  private startWorld: THREE.Vector3 | null = null

  // Preview
  private previewGroup: THREE.Group
  private previewMesh: THREE.Mesh | null = null

  constructor(toolManager: ToolManager, scene: THREE.Scene, config: DrawEllipseConfig) {
    this.toolManager = toolManager
    this.config = config

    this.previewGroup = new THREE.Group()
    this.previewGroup.name = 'draw-ellipse-preview'
    scene.add(this.previewGroup)
  }

  activate(): void {
    const selMgr = getViewer()?.getScene()?.getSelectionManager()
    selMgr?.deselect()
    selMgr?.clearHover()
    selMgr?.setEnabled(false)
    debug.log('DrawEllipseTool', 'Activated')
  }

  deactivate(): void {
    this.cancelDrawing()
    if (getToolMode() === 'pointer') {
      getViewer()?.getScene()?.getSelectionManager()?.setEnabled(true)
    }
    debug.log('DrawEllipseTool', 'Deactivated')
  }

  onMouseDown(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return
    if (!ctx.worldPoint) return

    this.isDrawing = true
    this.startWorld = ctx.worldPoint.clone()
    this.toolManager.setControlsEnabled(false)

    ctx.event.preventDefault()
    ctx.event.stopPropagation()

    debug.log('DrawEllipseTool', `Start at (${ctx.worldPoint.x.toFixed(2)}, ${ctx.worldPoint.z.toFixed(2)})`)
  }

  onMouseMove(ctx: ToolContext): void {
    if (!this.isDrawing || !this.startWorld || !ctx.worldPoint) return
    this.updatePreview(this.startWorld, ctx.worldPoint, ctx.camera)
  }

  onMouseUp(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return
    if (!this.isDrawing || !this.startWorld) {
      this.cancelDrawing()
      return
    }

    const endWorld = ctx.worldPoint
    if (!endWorld) {
      this.cancelDrawing()
      return
    }

    // Calculate bounds in world space
    const minX = Math.min(this.startWorld.x, endWorld.x)
    const maxX = Math.max(this.startWorld.x, endWorld.x)
    const minZ = Math.min(this.startWorld.z, endWorld.z)
    const maxZ = Math.max(this.startWorld.z, endWorld.z)

    const widthWorld = maxX - minX
    const depthWorld = maxZ - minZ

    // Check minimum size
    if (widthWorld < MIN_SIZE_WORLD || depthWorld < MIN_SIZE_WORLD) {
      debug.log('DrawEllipseTool', 'Too small, cancelled')
      this.cancelDrawing()
      return
    }

    // Convert to editor coords: center + size in dots
    const centerX = ((minX + maxX) / 2) * DPM
    const centerY = ((minZ + maxZ) / 2) * DPM

    // Create shape via callback (bounding box of the ellipse)
    const id = this.config.onCreate({
      x: Math.round(centerX),
      y: Math.round(centerY),
      width: Math.round(widthWorld * DPM),
      height: Math.round(depthWorld * DPM)
    })

    debug.log('DrawEllipseTool', `Created shape: ${id}`)

    // Clean up and switch back to select tool
    this.cancelDrawing()
    this.toolManager.setActiveTool('select')
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.cancelDrawing()
      this.toolManager.setActiveTool('select')
    }
  }

  dispose(): void {
    this.cancelDrawing()
    this.previewGroup.parent?.remove(this.previewGroup)
  }

  // --- Private ---

  private cancelDrawing(): void {
    if (this.isDrawing) {
      this.toolManager.setControlsEnabled(true)
    }
    this.isDrawing = false
    this.startWorld = null
    this.clearPreview()
    this.config.overlay?.hideAll()
  }

  private updatePreview(start: THREE.Vector3, end: THREE.Vector3, camera?: THREE.Camera): void {
    this.clearPreview()

    const minX = Math.min(start.x, end.x)
    const maxX = Math.max(start.x, end.x)
    const minZ = Math.min(start.z, end.z)
    const maxZ = Math.max(start.z, end.z)

    const width = maxX - minX
    const depth = maxZ - minZ

    if (width < 0.05 || depth < 0.05) return

    // Create ellipse shape inscribed in the bounding box
    const shape = new THREE.Shape()
    const rx = width / 2
    const rz = depth / 2

    for (let i = 0; i <= ELLIPSE_SEGMENTS; i++) {
      const angle = (i / ELLIPSE_SEGMENTS) * Math.PI * 2
      const x = Math.cos(angle) * rx
      const y = Math.sin(angle) * rz
      if (i === 0) {
        shape.moveTo(x, y)
      } else {
        shape.lineTo(x, y)
      }
    }

    const geometry = new THREE.ShapeGeometry(shape)
    const material = new THREE.MeshBasicMaterial({
      color: this.config.previewColor ?? 0x4ecca3,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    this.previewMesh = new THREE.Mesh(geometry, material)
    this.previewMesh.rotation.x = -Math.PI / 2
    this.previewMesh.position.set(
      (minX + maxX) / 2,
      0.02,
      (minZ + maxZ) / 2
    )

    this.previewGroup.add(this.previewMesh)

    // Show live measurement overlays
    if (camera && this.config.overlay) {
      this.config.overlay.showDimensions(width, depth, maxX, maxZ, camera)
      const area = Math.PI * (width / 2) * (depth / 2)
      this.config.overlay.showArea(area, (minX + maxX) / 2, (minZ + maxZ) / 2, camera)
    }
  }

  private clearPreview(): void {
    if (this.previewMesh) {
      this.previewGroup.remove(this.previewMesh)
      this.previewMesh.geometry.dispose()
      if (this.previewMesh.material instanceof THREE.Material) {
        this.previewMesh.material.dispose()
      }
      this.previewMesh = null
    }
  }
}
